import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  invalidateBlockApprovalRequestQueries,
  invalidateSpotApprovalRequestQueries,
} from '~/hooks/bidding/queryKeys'
import type { HoldForBulkWithdraw } from '~/pages/SpotBidding/hooks/useSpotWithdrawResultNotices'
import { useWithdrawBiddingBlockBidGroups } from '~/pages/SpotBidding/hooks/useWithdrawBiddingBlockBidGroups'
import { useWithdrawBiddingSpotBids } from '~/pages/SpotBidding/hooks/useWithdrawBiddingSpotBids'
import {
  type BulkWithdrawOutcome,
  shouldReloadListsAutomatically,
  summarizeBulkWithdraw,
} from '~/pages/SpotBidding/models/bulkWithdrawResult'
import type { BulkWithdrawRequest } from '~/pages/SpotBidding/models/bulkWithdrawTargets'
import { bulkWithdrawAcceptedKeys } from '~/pages/SpotBidding/models/spotBulkWithdrawNotice'

export type BulkWithdrawBidding = {
  /** 取下 RPC が実行中か (確認ダイアログの確認ボタンを無効にする)。 */
  isRunning: boolean
  /**
   * 通常入札とブロック入札を別の RPC で同時に取り下げ、 受け付けなかった札・結果を確認できなかった
   * 札があれば `outcome` に残す。 全件を受け付けた時は残さない。 片方の RPC が失敗してももう片方は
   * 止めない。 一覧は、 内容だけ先に更新されていた札が無ければ両方とも読み直し (受け付けた札の
   * 取下処理中・状態が進んでいた札を出すため)、 ある時は読み直さず「再読み込み」 を待つ
   * ([[shouldReloadListsAutomatically]])。 読み直しが終わるまで `isRunning` は続き、 `outcome` も出さない。
   */
  withdraw: (request: BulkWithdrawRequest) => Promise<void>
  /** 直近の実行結果 (表示中の受渡日のもの)。 閉じるか、 次の一括取下を始めるまで残る。 */
  outcome: BulkWithdrawOutcome | undefined
  dismissOutcome: () => void
  /** 結果のバナーの「再読み込み」: 通常入札とブロック入札の両方の一覧を 1 回で読み直し、 バナーを閉じる。 */
  reloadLists: () => void
}

/**
 * 入札中の札の一括取下 (AC-BSP-116) の実行と結果。 `useBulkApprovalFlow` と違い、 開閉と対象の選択は
 * 持たない (対象は確認ダイアログを開いた時点で確定している)。 結果は受渡日ごとに切り替える
 * (`useBulkApprovalFlow` の `lastOutcome` と同じ dateKey の扱い)。
 */
export const useBulkWithdrawBidding = (
  targetDate: Date,
  /**
   * 送る札の札ごとの通知を応答を待つ間止め、 両方の RPC の応答がそろった直後 (一覧を読み直す前) に
   * 受け付けた札を渡して、 その結果を件数のトーストで追わせる (AC-BSP-116)。
   */
  holdNotices: HoldForBulkWithdraw,
): BulkWithdrawBidding => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync: withdrawSpotBids } = useWithdrawBiddingSpotBids()
  const { mutateAsync: withdrawBlockBidGroups } =
    useWithdrawBiddingBlockBidGroups()
  const [isRunning, setRunning] = useState(false)
  const [lastOutcome, setLastOutcome] = useState<
    { dateKey: number; outcome: BulkWithdrawOutcome } | undefined
  >(undefined)

  const reloadBothLists = async (): Promise<void> => {
    await Promise.all([
      invalidateSpotApprovalRequestQueries(queryClient),
      invalidateBlockApprovalRequestQueries(queryClient),
    ])
  }

  const withdraw = async (request: BulkWithdrawRequest): Promise<void> => {
    const dateKey = targetDate.getTime()
    setRunning(true)
    setLastOutcome(undefined)
    const settleHeld = holdNotices([
      ...request.spot.map((target) => target.id),
      ...request.block.map((target) => target.id),
    ])
    try {
      const [spot, block] = await Promise.allSettled([
        request.spot.length > 0
          ? withdrawSpotBids({ targets: [...request.spot] })
          : Promise.resolve({ results: [] }),
        request.block.length > 0
          ? withdrawBlockBidGroups({ targets: [...request.block] })
          : Promise.resolve({ results: [] }),
      ])
      settleHeld(bulkWithdrawAcceptedKeys(spot, block))
      const outcome = summarizeBulkWithdraw({ request, spot, block, t })
      // 結果が分からないバナーは「一覧を読み直しました」 と言う (AC-BSP-116) ので、 読み直しが終わって
      // から出す。
      if (shouldReloadListsAutomatically(outcome)) await reloadBothLists()
      if (outcome !== undefined) setLastOutcome({ dateKey, outcome })
    } finally {
      setRunning(false)
    }
  }

  return {
    isRunning,
    withdraw,
    outcome:
      lastOutcome?.dateKey === targetDate.getTime()
        ? lastOutcome.outcome
        : undefined,
    dismissOutcome: () => setLastOutcome(undefined),
    reloadLists: () => {
      setLastOutcome(undefined)
      void reloadBothLists()
    },
  }
}
